/**
 * Shared builders for client-facing Task resources.
 *
 * Used by MeasuresTab (assessment assignment + Take Now), FormsFilesTab
 * (form request), and the Rules Engine. Ensures Tasks created by any
 * producer have a consistent shape that the Client Portal recognizes.
 */

import type { MedplumClient } from '@medplum/core';
import type { Extension, Meta, Patient, Practitioner, Reference, Task } from '@medplum/fhirtypes';
import {
  clearAssessmentSchedule,
  setAssessmentNextSend,
  setAssessmentSchedule,
  type RecurringAssessmentFrequency,
} from '@unify/fhir-model/resources/assessment-task';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';

import { getTaskTypeCoding } from '../pages/tasks/services/taskFhirMapping';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from './caseloadFhirConstants';
import {
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_DOCUMENT_COMPLETION_CODE,
  CLIENT_FACING_TASK_TAG_CODE,
  CLIENT_FACING_TASK_TAG_SYSTEM,
  CLIENT_FORM_ASSIGNMENT_CODE,
  CLIENT_SIGNATURE_REQUEST_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  RULES_ENGINE_CODE_SYSTEM,
  RULES_ENGINE_FORM_ASSIGNMENT_CODE,
  TASK_TYPE_CODE_SYSTEM,
} from './taskFhirConstants';
import {
  ASSESSMENT_FREQUENCIES,
  isRecurringFrequency,
  type AssessmentFrequency,
} from './assessmentRecurrence';

// ─── Types ────────────────────────────────────────────────────────────

export interface TaskBuilderParams {
  patientId: string;
  questionnaireId: string;
  questionnaireDisplay: string;
  practitioner?: Practitioner | null;
  dueDate?: string;
  episodeId?: string;
  description?: string;
  /**
   * Patient compartment CareTeam refs (`Patient.meta.accounts` / legacy `meta.account`).
   * Dual-compartment patients (e.g. co-managed panels) must pass **all** refs so every
   * provider membership can read/write the same Task under `%care_team` policies.
   */
  accounts?: Reference[];
  /** Recurrence frequency. */
  frequency?: string;
  /** End date for recurring schedule (YYYY-MM-DD). */
  recurrenceEndDate?: string;
  /** Pre-computed next send date (ISO datetime). */
  nextSendDate?: string;
}

// ─── Internal helpers ─────────────────────────────────────────────────

function practitionerRef(p: Practitioner | null | undefined) {
  if (!p?.id) return undefined;
  const display = formatPractitionerPreferredLabel(p) || undefined;
  return {
    reference: `Practitioner/${p.id}` as const,
    ...(display ? { display } : {}),
  };
}

function episodeExtension(episodeId: string | undefined) {
  if (!episodeId) return undefined;
  return [
    {
      url: EPISODE_OF_CARE_CONTEXT_EXTENSION,
      valueReference: { reference: `EpisodeOfCare/${episodeId}` },
    },
  ];
}

function clientFacingTag() {
  return { system: CLIENT_FACING_TASK_TAG_SYSTEM, code: CLIENT_FACING_TASK_TAG_CODE };
}

// ─── Public builders ──────────────────────────────────────────────────

/**
 * Build a Task for assigning a scored measure to a client.
 */
export function buildAssessmentAssignmentTask(params: TaskBuilderParams): Task {
  const {
    patientId,
    questionnaireId,
    questionnaireDisplay,
    practitioner: pract,
    dueDate,
    episodeId,
    description,
    accounts,
    frequency,
    recurrenceEndDate,
    nextSendDate,
  } = params;

  const requester = practitionerRef(pract);
  const exts: Extension[] = [...(episodeExtension(episodeId) ?? [])];
  const task: Task = {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    code: {
      coding: [
        { system: TASK_TYPE_CODE_SYSTEM, code: CLIENT_ASSESSMENT_ASSIGNMENT_CODE, display: 'Client Assessment Assignment' },
      ],
    },
    description: description ?? questionnaireDisplay,
    for: { reference: `Patient/${patientId}` },
    focus: { reference: `Questionnaire/${questionnaireId}`, display: questionnaireDisplay },
    ...(requester ? { requester } : {}),
    authoredOn: new Date().toISOString(),
    ...(dueDate ? { restriction: { period: { end: dueDate } } } : {}),
    ...(exts.length > 0 ? { extension: exts } : {}),
    meta: {
      // Routes WS subscription events to the Patient's compartment (same pattern as messaging).
      // Without this, Medplum silently drops events when a Practitioner creates the Task.
      account: { reference: `Patient/${patientId}` },
      tag: [clientFacingTag()],
      ...(accounts?.length ? { accounts } : {}),
    },
  };
  if (frequency && isRecurringFrequency(frequency)) {
    setAssessmentSchedule(
      task,
      frequency as RecurringAssessmentFrequency,
      nextSendDate ?? task.authoredOn!,
      recurrenceEndDate,
    );
  } else if (nextSendDate) {
    setAssessmentNextSend(task, nextSendDate);
  }
  return task;
}

/**
 * Build a Task for the provider "Take Now" flow (provider-administered assessment).
 * Uses CLIENT_TAKE_NOW_ASSESSMENT_CODE so it is excluded from the Schedule Assessments panel
 * and only appears in the Assessment Log after completion.
 */
export function buildProviderTakeNowTask(params: TaskBuilderParams): Task {
  const base = buildAssessmentAssignmentTask(params);
  return {
    ...base,
    code: {
      coding: [
        { system: TASK_TYPE_CODE_SYSTEM, code: CLIENT_TAKE_NOW_ASSESSMENT_CODE, display: 'Provider Take Now Assessment' },
      ],
    },
  };
}

/**
 * Build a Task for requesting a client-facing form (intake, consent, etc.).
 */
export function buildFormRequestTask(params: TaskBuilderParams & { message?: string }): Task {
  const {
    patientId,
    questionnaireId,
    questionnaireDisplay,
    practitioner: pract,
    dueDate,
    episodeId,
    description,
    message,
    accounts,
  } = params;

  const requester = practitionerRef(pract);
  const extensions = episodeExtension(episodeId);

  return {
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    code: {
      coding: [
        { system: TASK_TYPE_CODE_SYSTEM, code: CLIENT_FORM_ASSIGNMENT_CODE, display: 'Client Form Assignment' },
      ],
    },
    description: description ?? questionnaireDisplay,
    for: { reference: `Patient/${patientId}` },
    focus: { reference: `Questionnaire/${questionnaireId}`, display: questionnaireDisplay },
    ...(requester ? { requester } : {}),
    authoredOn: new Date().toISOString(),
    ...(dueDate ? { restriction: { period: { end: dueDate } } } : {}),
    ...(extensions ? { extension: extensions } : {}),
    ...(message ? { note: [{ text: message }] } : {}),
    meta: {
      account: { reference: `Patient/${patientId}` },
      tag: [clientFacingTag()],
      ...(accounts?.length ? { accounts } : {}),
    },
  };
}

// ─── Compartment helpers ──────────────────────────────────────────────

function dedupeAccountRefs(refs: Reference[]): Reference[] {
  const seen = new Set<string>();
  const out: Reference[] = [];
  for (const r of refs) {
    const key = r.reference ?? '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/** Collect CareTeam (etc.) account refs from any resource `Meta` (Medplum `accounts` + legacy `account`). */
export function collectMetaAccounts(meta: Meta | undefined): Reference[] {
  if (!meta) return [];
  const plural = meta.accounts;
  if (plural?.length) {
    return dedupeAccountRefs(plural);
  }
  const single = meta.account;
  if (single?.reference) {
    return [single];
  }
  return [];
}

/** All compartment account refs on a Patient (dual `$set-accounts` → `meta.accounts`). */
export function collectPatientMetaAccounts(patient: Patient): Reference[] {
  return collectMetaAccounts(patient.meta);
}

/** Merge compartment account refs, preserving order and deduping by reference. */
export function mergeCompartmentAccountRefs(
  existing: Reference[],
  additions: Reference[],
): Reference[] {
  const seen = new Set<string>();
  const merged: Reference[] = [];
  for (const ref of [...existing, ...additions]) {
    const key = ref.reference;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    merged.push(ref);
  }
  return merged;
}

/** Copy Patient compartment refs onto a resource's meta.accounts (EoC, SR, Task, …). */
export function mergePatientAccountsOntoMeta(
  patient: Patient,
  meta: Meta | undefined,
): Meta {
  const accounts = collectPatientMetaAccounts(patient);
  if (accounts.length === 0) return meta ?? {};
  return { ...(meta ?? {}), accounts };
}

/**
 * Stamps resource meta.accounts for RBAC compartments after panel provisioning.
 * Merges patient compartments with the care-manager panel (and optional org).
 */
export function buildCompartmentMetaFromPatientAndPanel(
  patient: Patient,
  panelCareTeamId: string,
  organizationId?: string,
  existingMeta?: Meta,
): Meta {
  const additions: Reference[] = [{ reference: `CareTeam/${panelCareTeamId}` }];
  if (organizationId?.trim()) {
    additions.unshift({ reference: `Organization/${organizationId.trim()}` });
  }
  const accounts = mergeCompartmentAccountRefs(collectPatientMetaAccounts(patient), additions);
  if (accounts.length === 0) return existingMeta ?? {};
  return { ...(existingMeta ?? {}), accounts };
}

/**
 * Read Patient compartment refs for stamping Tasks / QuestionnaireResponses so
 * multi-CareTeam patients stay visible to every panel (Emily Rodriguez, Alex Rivera, …).
 */
export async function getPatientAccountRefs(
  medplum: MedplumClient,
  patientId: string,
): Promise<Reference[]> {
  const pt = await medplum.readResource('Patient', patientId);
  return collectPatientMetaAccounts(pt);
}

// ─── Utility readers ──────────────────────────────────────────────────

/** Extract the Questionnaire reference from a Task.focus. */
export function getQuestionnaireRefFromTask(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('Questionnaire/')) return undefined;
  return ref;
}

/** Extract the EpisodeOfCare reference from Task extensions. */
export function getTaskEpisodeRef(task: Task): string | undefined {
  return task.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION)
    ?.valueReference?.reference;
}

/** True if a Task is a client-facing questionnaire assignment (assessment or form). */
export function isClientFacingQuestionnaireTask(task: Task): boolean {
  if (!task.focus?.reference?.startsWith('Questionnaire/')) return false;

  const tags = task.meta?.tag ?? [];
  if (tags.some((t) => t.system === CLIENT_FACING_TASK_TAG_SYSTEM && t.code === CLIENT_FACING_TASK_TAG_CODE)) {
    return true;
  }

  const codings = task.code?.coding ?? [];
  if (codings.some((c) =>
    c.system === TASK_TYPE_CODE_SYSTEM && (
      c.code === CLIENT_FORM_ASSIGNMENT_CODE ||
      c.code === CLIENT_ASSESSMENT_ASSIGNMENT_CODE ||
      c.code === CLIENT_TAKE_NOW_ASSESSMENT_CODE
    ),
  )) {
    return true;
  }

  return false;
}

// ─── Canonical Task-kind classification ──────────────────────────────
//
// Schedule Assessments panel  → isScheduledAssessmentTask
// Assessment Log              → isScheduledAssessmentTask OR isTakeNowAssessmentTask
// Forms & Files               → isFormRequestTask
// Client Portal Tasks         → all client-facing (isClientFacingQuestionnaireTask)

/**
 * True if the Task is a scheduled assessment series created from the Schedule Assessments
 * panel. Uses CLIENT_ASSESSMENT_ASSIGNMENT_CODE.
 * Form requests and Take Now tasks are NOT scheduled assessment tasks.
 */
export function isScheduledAssessmentTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) => c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  );
}

/**
 * True if the Task is a provider-administered Take Now assessment.
 * Uses CLIENT_TAKE_NOW_ASSESSMENT_CODE.
 * Does NOT appear in Schedule Assessments; appears in Assessment Log after completion.
 */
export function isTakeNowAssessmentTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) => c.system === TASK_TYPE_CODE_SYSTEM && c.code === CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  );
}

const TASK_DESCRIPTION_PREFIX = /^(?:complete|sign):\s*/i;

/** Human-readable form name for chart and Forms & Files (strips "Complete:" / "Sign:" prefixes). */
export function getFormTaskDisplayName(task: Task): string {
  const focusDisplay = task.focus?.display?.trim();
  if (focusDisplay) return focusDisplay;

  const description = task.description?.trim();
  if (description) {
    const stripped = description.replace(TASK_DESCRIPTION_PREFIX, '').trim();
    if (stripped) return stripped;
  }

  return getTaskTypeCoding(task)?.display ?? 'Form';
}

export type ChartFormTaskStatus = 'requested' | 'in-progress' | 'overdue' | 'completed' | 'cancelled';

/** Map a FHIR Task to a chart overview status label. */
export function resolveChartFormTaskStatus(task: Task): ChartFormTaskStatus {
  if (task.status === 'completed') return 'completed';
  if (task.status === 'cancelled') return 'cancelled';

  const dueStr = task.restriction?.period?.end?.slice(0, 10);
  if (dueStr && new Date(`${dueStr}T23:59:59`).getTime() < Date.now()) {
    return 'overdue';
  }
  if (task.status === 'in-progress') return 'in-progress';
  return 'requested';
}

export function chartFormTaskStatusLabel(status: ChartFormTaskStatus): string {
  switch (status) {
    case 'in-progress':
      return 'In progress';
    case 'overdue':
      return 'Overdue';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Requested';
  }
}

export function chartFormTaskStatusColor(status: ChartFormTaskStatus): string {
  switch (status) {
    case 'overdue':
      return 'red';
    case 'in-progress':
      return 'blue';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'gray';
    default:
      return 'yellow';
  }
}

/**
 * True if the Task is a form request (intake, consent, signature, etc.).
 * Covers manual form requests and Rules Engine form assignments.
 * Appears in Forms & Files and Client Portal; NOT in Schedule Assessments.
 */
export function isFormRequestTask(task: Task): boolean {
  const codings = task.code?.coding ?? [];
  return codings.some(
    (c) =>
      (c.system === TASK_TYPE_CODE_SYSTEM && (
        c.code === CLIENT_FORM_ASSIGNMENT_CODE ||
        c.code === CLIENT_SIGNATURE_REQUEST_CODE ||
        c.code === CLIENT_DOCUMENT_COMPLETION_CODE
      )) ||
      (c.system === RULES_ENGINE_CODE_SYSTEM && c.code === RULES_ENGINE_FORM_ASSIGNMENT_CODE),
  );
}

/** True if the Task is any kind of assessment (scheduled or Take Now). */
export function isAnyAssessmentTask(task: Task): boolean {
  return isScheduledAssessmentTask(task) || isTakeNowAssessmentTask(task);
}

/**
 * True if the Task is a **standing order** — a recurring series with a frequency extension.
 * Standing orders are shown in the Schedule Assessments panel but NOT in the Client Portal
 * task list (clients see occurrence Tasks created by the Recurring Assessment Bot instead).
 *
 * Canonical implementation: `@unify/fhir-model/resources/assessment-task`.
 */
export {
  isRecurringStandingOrder,
  isAssessmentOccurrenceTask,
  isOneTimeAssessmentTask,
  isBillableAssessmentAssignment,
  classifyAssessmentTask,
} from '@unify/fhir-model/resources/assessment-task';

// Re-export for consumers that only import from this module
export { ASSESSMENT_FREQUENCIES, isRecurringFrequency, type AssessmentFrequency };

/**
 * Build a one-time occurrence Task from a recurring standing-order Task.
 * Used by the Recurring Assessment Bot and by the provider Schedule Assessment
 * panel when creating an immediate first send.
 *
 * The occurrence Task:
 * - Uses `CLIENT_ASSESSMENT_ASSIGNMENT_CODE` (visible to client portal)
 * - Has NO frequency/recurrence extensions
 * - Has `partOf` reference to the standing-order Task for deduplication / filtering
 * - Carries the same patient, questionnaire, episode, and account as the parent
 */
export function buildAssessmentOccurrenceTask(
  standingOrderTask: Task,
  dueDate?: string,
): Task {
  const occurrence: Task = {
    ...standingOrderTask,
    id: undefined,
    resourceType: 'Task',
    status: 'requested',
    intent: 'order',
    code: {
      coding: [
        {
          system: TASK_TYPE_CODE_SYSTEM,
          code: CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
          display: 'Client Assessment Assignment',
        },
      ],
    },
    authoredOn: new Date().toISOString(),
    // Link back to standing order so the schedule panel and submission bot can
    // distinguish occurrences from one-time scheduled assignments.
    ...(standingOrderTask.id ? { partOf: [{ reference: `Task/${standingOrderTask.id}` }] } : {}),
    meta: {
      ...(standingOrderTask.meta ?? {}),
      tag: [{ system: CLIENT_FACING_TASK_TAG_SYSTEM, code: CLIENT_FACING_TASK_TAG_CODE }],
    },
  };
  clearAssessmentSchedule(occurrence);
  if (occurrence.input?.length === 0) occurrence.input = undefined;
  occurrence.restriction = dueDate ? { period: { end: dueDate } } : undefined;
  return occurrence;
}
